/**
 * Leagues screen (SPEC §12): league list, live division standings + calendar,
 * end-of-season archives with promotions/relegations, and — for admins —
 * league creation, season creation (calendar + roster), launching a division
 * round, and closing a season. All server I/O goes through callbacks so main.ts
 * owns the WebSocket; this module owns only the leagues-screen DOM + navigation.
 */

import type {
  CircuitSummary,
  DivisionStandingView,
  LeagueSummary,
  PlayerDirectoryEntry,
  SeasonArchive,
  SeasonView,
} from '@microligue/shared';
import { applyLiveryToCarConfig, defaultCarConfig } from '@microligue/shared';
import { carSwatch } from '../car/svg';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function divisionName(level: number): string {
  return `Division ${level + 1}`;
}

export interface LeagueDeps {
  isAdmin: () => boolean;
  /** Validated circuits, for building a season calendar. */
  circuits: () => CircuitSummary[];
  /** Player directory, for building a season roster (fetched lazily). */
  fetchPlayers: () => Promise<PlayerDirectoryEntry[]>;
  send: {
    listLeagues: () => void;
    createLeague: (input: {
      name: string;
      divisions: number;
      promoteN: number;
      relegateN: number;
    }) => void;
    getSeason: (seasonId: number) => void;
    createSeason: (
      leagueId: number,
      circuitIds: string[],
      assignments: { playerId: number; divisionLevel: number }[],
    ) => void;
    launchRound: (seasonId: number, divisionLevel: number) => void;
    closeSeason: (seasonId: number) => void;
    getArchives: (leagueId: number) => void;
    getArchive: (seasonId: number) => void;
  };
  onOpenProfile: (pseudo: string) => void;
  onBack: () => void;
  status: (msg: string) => void;
}

type View =
  | { kind: 'list' }
  | { kind: 'createLeague' }
  | { kind: 'createSeason'; league: LeagueSummary }
  | { kind: 'season'; view: SeasonView }
  | { kind: 'archives'; leagueId: number; leagueName: string; seasons: SeasonListEntry[] }
  | { kind: 'archive'; archive: SeasonArchive };

interface SeasonListEntry {
  id: number;
  number: number;
  status: 'running' | 'finished';
}

export class LeagueScreen {
  private leagues: LeagueSummary[] = [];
  private view: View = { kind: 'list' };
  /** Division id per level, from the league's currentSeason (server truth). */

  constructor(
    private readonly root: HTMLElement,
    private readonly deps: LeagueDeps,
  ) {}

  /** Enter the screen from the menu: always land on the league list. */
  open(): void {
    this.view = { kind: 'list' };
    this.deps.send.listLeagues();
    this.render();
  }

  // --- server pushes -------------------------------------------------------

  setLeagues(leagues: LeagueSummary[], visible: boolean): void {
    this.leagues = leagues;
    // Only touch the UI when the leagues screen is actually on screen. A
    // background `leagues` broadcast (e.g. a round ending elsewhere while the
    // player is IN a race) must not re-render or, worse, re-request a season
    // that would yank the player off the game screen.
    if (!visible) return;
    if (this.view.kind === 'list') this.render();
    else if (this.view.kind === 'season' && this.view.view.status === 'running') {
      this.deps.send.getSeason(this.view.view.id);
    }
  }

  setSeason(view: SeasonView): void {
    this.view = { kind: 'season', view };
    this.render();
  }

  setArchives(leagueId: number, seasons: SeasonListEntry[]): void {
    const name = this.leagues.find((l) => l.id === leagueId)?.name ?? 'Ligue';
    this.view = { kind: 'archives', leagueId, leagueName: name, seasons };
    this.render();
  }

  setArchive(archive: SeasonArchive): void {
    this.view = { kind: 'archive', archive };
    this.render();
  }

  // --- rendering -----------------------------------------------------------

  private render(): void {
    this.root.replaceChildren();
    switch (this.view.kind) {
      case 'list':
        this.renderList();
        break;
      case 'createLeague':
        this.renderCreateLeague();
        break;
      case 'createSeason':
        void this.renderCreateSeason(this.view.league);
        break;
      case 'season':
        this.renderSeason(this.view.view);
        break;
      case 'archives':
        this.renderArchivesList(this.view.leagueId, this.view.leagueName, this.view.seasons);
        break;
      case 'archive':
        this.renderArchive(this.view.archive);
        break;
    }
  }

  private backButton(to: () => void, label = '← Retour'): HTMLElement {
    const btn = el('button', 'stats-back', label);
    btn.addEventListener('click', to);
    return btn;
  }

  private renderList(): void {
    this.root.appendChild(this.backButton(() => this.deps.onBack(), '← Menu'));
    const head = el('div', 'stats-header');
    head.appendChild(el('h2', undefined, '🏆 Ligues'));
    this.root.appendChild(head);

    if (this.deps.isAdmin()) {
      const create = el('button', 'btn-primary', '➕ Créer une ligue');
      create.addEventListener('click', () => {
        this.view = { kind: 'createLeague' };
        this.render();
      });
      this.root.appendChild(create);
    }

    if (this.leagues.length === 0) {
      this.root.appendChild(el('div', 'stats-empty', 'Aucune ligue pour l’instant.'));
      return;
    }

    for (const lg of this.leagues) {
      const card = el('div', 'league-card');
      const meta = el('div', 'league-meta');
      if (lg.livery) {
        meta.appendChild(
          carSwatch(applyLiveryToCarConfig(defaultCarConfig('#888888'), lg.livery), 40),
        );
      }
      const info = el('div');
      info.appendChild(el('div', 'league-name', lg.name));
      const sub =
        `${lg.divisionsCount} division(s) · +${lg.promoteN}/−${lg.relegateN}` +
        (lg.currentSeason
          ? ` · saison ${lg.currentSeason.number} (${lg.currentSeason.status === 'running' ? 'en cours' : 'terminée'})`
          : ' · aucune saison');
      info.appendChild(el('div', 'league-sub', sub));
      meta.appendChild(info);
      card.appendChild(meta);

      const actions = el('div', 'league-actions');
      if (lg.currentSeason) {
        const view = el('button', undefined, 'Classement');
        view.addEventListener('click', () => this.deps.send.getSeason(lg.currentSeason!.id));
        actions.appendChild(view);
      }
      const arch = el('button', undefined, 'Archives');
      arch.addEventListener('click', () => this.deps.send.getArchives(lg.id));
      actions.appendChild(arch);

      if (this.deps.isAdmin()) {
        const season = el('button', undefined, '➕ Saison');
        season.title = 'Ouvrir une nouvelle saison (calendrier + inscriptions)';
        season.addEventListener('click', () => {
          this.view = { kind: 'createSeason', league: lg };
          this.render();
        });
        actions.appendChild(season);
      }
      card.appendChild(actions);
      this.root.appendChild(card);
    }
  }

  private renderCreateLeague(): void {
    this.root.appendChild(
      this.backButton(() => {
        this.view = { kind: 'list' };
        this.render();
      }),
    );
    this.root.appendChild(el('h2', undefined, 'Créer une ligue'));

    const form = el('div', 'league-form');
    const name = el('input');
    name.maxLength = 40;
    name.placeholder = 'Nom de la ligue';
    const divisions = numberField('Divisions', 2, 1, 8);
    const promote = numberField('Montées / division', 2, 0, 8);
    const relegate = numberField('Descentes / division', 2, 0, 8);

    form.append(
      labeled('Nom', name),
      labeled('Divisions', divisions),
      labeled('Montées', promote),
      labeled('Descentes', relegate),
    );
    this.root.appendChild(form);

    const submit = el('button', 'btn-primary', 'Créer la ligue');
    submit.addEventListener('click', () => {
      const nm = name.value.trim();
      if (!nm) {
        this.deps.status('Donnez un nom à la ligue.');
        return;
      }
      this.deps.send.createLeague({
        name: nm,
        divisions: clampInt(divisions.value, 1, 8, 2),
        promoteN: clampInt(promote.value, 0, 8, 2),
        relegateN: clampInt(relegate.value, 0, 8, 2),
      });
      this.deps.status('Ligue créée ✓');
      this.view = { kind: 'list' };
      this.render();
    });
    this.root.appendChild(submit);
    this.root.appendChild(
      el(
        'div',
        'stats-sub',
        'Le barème par défaut (10-8-6-5-4-3-2-1 + 1 au meilleur tour) et la livrée tricolore du champion sont appliqués.',
      ),
    );
  }

  private async renderCreateSeason(league: LeagueSummary): Promise<void> {
    this.root.appendChild(
      this.backButton(() => {
        this.view = { kind: 'list' };
        this.render();
      }),
    );
    this.root.appendChild(el('h2', undefined, `Nouvelle saison — ${league.name}`));

    // Calendar builder: ordered list of validated circuits.
    const validated = this.deps.circuits().filter((c) => c.status === 'validated');
    if (validated.length === 0) {
      this.root.appendChild(
        el('div', 'stats-empty', 'Aucun circuit validé — validez-en dans l’atelier d’abord.'),
      );
      return;
    }

    this.root.appendChild(el('h3', undefined, 'Calendrier'));
    const calendar: string[] = [];
    const calendarList = el('div', 'season-calendar');
    const circuitSelect = el('select');
    for (const c of validated) {
      const opt = el('option');
      opt.value = c.id;
      opt.textContent = c.name;
      circuitSelect.appendChild(opt);
    }
    const addRound = el('button', undefined, '➕ Ajouter la manche');
    const renderCalendar = (): void => {
      calendarList.replaceChildren();
      if (calendar.length === 0) {
        calendarList.appendChild(
          el('span', 'stats-sub', 'Aucune manche — ajoutez au moins un circuit.'),
        );
      }
      calendar.forEach((cid, i) => {
        const chip = el('span', 'season-chip');
        const nm = validated.find((c) => c.id === cid)?.name ?? cid;
        chip.appendChild(document.createTextNode(`${i + 1}. ${nm}`));
        const rm = el('button', 'chip-remove', '✕');
        rm.addEventListener('click', () => {
          calendar.splice(i, 1);
          renderCalendar();
        });
        chip.appendChild(rm);
        calendarList.appendChild(chip);
      });
    };
    addRound.addEventListener('click', () => {
      if (circuitSelect.value) {
        calendar.push(circuitSelect.value);
        renderCalendar();
      }
    });
    const calRow = el('div', 'season-cal-row');
    calRow.append(circuitSelect, addRound);
    this.root.append(calRow, calendarList);
    renderCalendar();

    // Roster builder: each player gets a division (or "not registered").
    this.root.appendChild(el('h3', undefined, 'Inscriptions'));
    const rosterBox = el('div', 'season-roster');
    rosterBox.appendChild(el('span', 'stats-sub', 'Chargement des joueurs…'));
    this.root.appendChild(rosterBox);

    const players = await this.deps.fetchPlayers();
    // The view may have changed while awaiting.
    if (this.view.kind !== 'createSeason' || this.view.league.id !== league.id) return;
    rosterBox.replaceChildren();
    const assign = new Map<number, number>(); // playerId → division level
    for (const p of players) {
      const row = el('div', 'roster-row');
      const nameBtn = el('button', 'link-btn', p.pseudo + (p.isAdmin ? ' 🛡' : ''));
      nameBtn.addEventListener('click', () => this.deps.onOpenProfile(p.pseudo));
      const sel = el('select');
      const none = el('option');
      none.value = '';
      none.textContent = '— non inscrit';
      sel.appendChild(none);
      for (let lvl = 0; lvl < league.divisionsCount; lvl++) {
        const opt = el('option');
        opt.value = String(lvl);
        opt.textContent = divisionName(lvl);
        sel.appendChild(opt);
      }
      sel.addEventListener('change', () => {
        if (sel.value === '') assign.delete(p.id);
        else assign.set(p.id, Number(sel.value));
      });
      row.append(nameBtn, sel);
      rosterBox.appendChild(row);
    }

    const submit = el('button', 'btn-primary', 'Lancer la saison');
    submit.addEventListener('click', () => {
      if (calendar.length === 0) {
        this.deps.status('Ajoutez au moins une manche au calendrier.');
        return;
      }
      if (assign.size === 0) {
        this.deps.status('Inscrivez au moins un joueur.');
        return;
      }
      // The server maps 0-based division levels to internal division ids.
      const assignments = [...assign.entries()].map(([playerId, level]) => ({
        playerId,
        divisionLevel: level,
      }));
      this.deps.status('Création de la saison…');
      this.deps.send.createSeason(league.id, [...calendar], assignments);
    });
    this.root.appendChild(submit);
  }

  private renderSeason(view: SeasonView): void {
    this.root.appendChild(
      this.backButton(() => {
        this.view = { kind: 'list' };
        this.deps.send.listLeagues();
        this.render();
      }),
    );
    const head = el('div', 'stats-header');
    head.appendChild(el('h2', undefined, `${view.leagueName} — saison ${view.number}`));
    head.appendChild(
      el(
        'span',
        `badge ${view.status === 'running' ? 'badge-running' : 'badge-done'}`,
        view.status === 'running' ? 'en cours' : 'terminée',
      ),
    );
    this.root.appendChild(head);

    // Calendar strip.
    const cal = el('div', 'season-calendar');
    for (const r of view.calendar) {
      const chip = el(
        'span',
        `season-chip${r.done >= view.divisionsCount ? ' done' : ''}`,
        `${r.index + 1}. ${r.circuitName} (${r.done}/${view.divisionsCount})`,
      );
      cal.appendChild(chip);
    }
    this.root.append(el('h3', undefined, 'Calendrier'), cal);

    // Standings per division + admin round launcher.
    for (const div of view.divisions) {
      const divHead = el('div', 'division-head');
      divHead.appendChild(el('h3', undefined, divisionName(div.level)));
      if (this.deps.isAdmin() && view.status === 'running') {
        const launch = el('button', undefined, '🏁 Lancer la prochaine manche');
        launch.addEventListener('click', () => {
          this.deps.send.launchRound(view.id, div.level);
          this.deps.status(`Manche lancée pour ${divisionName(div.level)} — rejoignez le salon.`);
        });
        divHead.appendChild(launch);
      }
      this.root.appendChild(divHead);
      this.root.appendChild(this.standingsTable(div));
    }

    if (this.deps.isAdmin() && view.status === 'running') {
      const close = el('button', 'btn-danger', '🏁 Clôturer la saison (montées/descentes + titre)');
      close.addEventListener('click', () => {
        this.deps.send.closeSeason(view.id);
        this.deps.status('Clôture de la saison…');
      });
      this.root.appendChild(close);
    }
  }

  private standingsTable(div: DivisionStandingView): HTMLElement {
    const wrap = el('div', 'stats-table-wrap');
    const t = el('table', 'stats-table');
    const head = el('tr');
    for (const h of ['#', 'Pilote', 'Pts', 'V', 'Pod.', 'Manches']) {
      head.appendChild(el('th', undefined, h));
    }
    t.appendChild(head);
    if (div.entries.length === 0) {
      const tr = el('tr');
      const td = el('td');
      td.colSpan = 6;
      td.textContent = 'Aucun pilote inscrit.';
      tr.appendChild(td);
      t.appendChild(tr);
    }
    div.entries.forEach((e, i) => {
      const tr = el('tr');
      const rank = el('td', undefined, String(i + 1));
      const nameTd = el('td');
      const nameBtn = el('button', 'link-btn', e.pseudo);
      nameBtn.addEventListener('click', () => this.deps.onOpenProfile(e.pseudo));
      nameTd.appendChild(nameBtn);
      tr.append(
        rank,
        nameTd,
        el('td', 'num', String(e.points)),
        el('td', 'num', String(e.wins)),
        el('td', 'num', String(e.podiums)),
        el('td', 'num', String(e.finishes.length)),
      );
      t.appendChild(tr);
    });
    wrap.appendChild(t);
    return wrap;
  }

  private renderArchivesList(
    leagueId: number,
    leagueName: string,
    seasons: SeasonListEntry[],
  ): void {
    this.root.appendChild(
      this.backButton(() => {
        this.view = { kind: 'list' };
        this.render();
      }),
    );
    this.root.appendChild(el('h2', undefined, `Archives — ${leagueName}`));
    if (seasons.length === 0) {
      this.root.appendChild(el('div', 'stats-empty', 'Aucune saison pour cette ligue.'));
      return;
    }
    for (const s of seasons) {
      const row = el('div', 'league-card');
      const info = el('div');
      info.appendChild(el('div', 'league-name', `Saison ${s.number}`));
      info.appendChild(el('div', 'league-sub', s.status === 'finished' ? 'terminée' : 'en cours'));
      row.appendChild(info);
      const open = el('button', undefined, s.status === 'finished' ? 'Palmarès' : 'Classement');
      open.addEventListener('click', () => {
        if (s.status === 'finished') this.deps.send.getArchive(s.id);
        else this.deps.send.getSeason(s.id);
      });
      row.appendChild(open);
      this.root.appendChild(row);
      void leagueId;
    }
  }

  private renderArchive(archive: SeasonArchive): void {
    this.root.appendChild(this.backButton(() => this.deps.send.getArchives(archive.leagueId)));
    this.root.appendChild(el('h2', undefined, `${archive.leagueName} — saison ${archive.number}`));
    const champ = el('div', 'archive-champion');
    champ.textContent = archive.champion ? `🏆 Champion : ${archive.champion}` : 'Sans champion';
    this.root.appendChild(champ);

    for (const div of archive.divisions) {
      this.root.appendChild(el('h3', undefined, divisionName(div.level)));
      this.root.appendChild(this.standingsTable(div));
    }

    this.root.appendChild(el('h3', undefined, 'Mouvements'));
    if (archive.movements.length === 0) {
      this.root.appendChild(el('div', 'stats-empty', 'Aucun mouvement.'));
    } else {
      const list = el('div', 'movement-list');
      for (const m of archive.movements) {
        if (m.kind === 'stayed') continue;
        const arrow = m.kind === 'promoted' ? '⬆' : '⬇';
        const cls = m.kind === 'promoted' ? 'promoted' : 'relegated';
        const row = el(
          'div',
          `movement ${cls}`,
          `${arrow} ${m.pseudo} : ${divisionName(m.fromLevel)} → ${divisionName(m.toLevel)}`,
        );
        list.appendChild(row);
      }
      this.root.appendChild(list);
    }
  }
}

// --- small form helpers ----------------------------------------------------

function numberField(_label: string, value: number, min: number, max: number): HTMLInputElement {
  const input = document.createElement('input');
  input.type = 'number';
  input.value = String(value);
  input.min = String(min);
  input.max = String(max);
  return input;
}

function labeled(label: string, control: HTMLElement): HTMLElement {
  const wrap = el('label', 'league-field');
  wrap.appendChild(document.createTextNode(label));
  wrap.appendChild(control);
  return wrap;
}

function clampInt(raw: string, min: number, max: number, fallback: number): number {
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}
